import React, { useEffect, useState } from 'react';
import { arrayOf, func, shape } from 'prop-types';
import { useNotification } from '@ancert/aui-notification-context';
import { useForm } from 'react-hook-form';
import { AUITable } from '@ancert/aui-table';
import AUIButton from '@ancert/aui-button';
import AUITextAreaField from '@ancert/aui-input-textarea-field';
import ModalAsignarDenuncia from '../../../components/ModalAsignarDenuncia';
import getColumns from './columns';
import { defaultValues, resolver } from './form';
import {
  deleteDesasignarUsuario,
  getAutoridades,
  postMensajesDenuncia,
  putStatus,
} from '../../../services/denuncias.service';
import { PAGE_SIZE } from '../../../config';
import { ESTADO_DENUNCIA } from '../../../lib/denuncia.enum';

const GestionDenuncia = ({ data, reloadMensajes, reloadComplaint }) => {
  const { openNotification } = useNotification();
  const [isEnable, setEnabled] = useState(true);
  const [texto, setTexto] = useState('');
  const [tableData, setTableData] = useState([]);
  const [isShow, setShow] = useState(false);
  const [isMotivo, setMotivo] = useState(false);
  const [isDesestimar, setDesestimar] = useState(false);
  const [isProcesar, setProcesar] = useState(false);
  const [isFinalizar, setFinalizar] = useState(false);

  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues,
    resolver: resolver({ isMotivo }),
    mode: 'onSubmit',
  });

  const handleChange = (event) => {
    setTexto(event.target.value);
  };

  const getAssignedAutoridades = async () => {
    const autoridades = await getAutoridades({ id: data.id });
    setTableData(autoridades.data);
  };

  const onProcesar = () => {
    setProcesar(true);
    setDesestimar(false);
    setFinalizar(false);
    setEnabled(false);
    setMotivo(false);
    reset(defaultValues);
  };

  const onDesestimar = () => {
    setProcesar(false);
    setFinalizar(false);
    setDesestimar(true);
    setEnabled(false);
    setMotivo(true);
  };

  const onFinalizar = () => {
    setDesestimar(false);
    setProcesar(false);
    setFinalizar(true);
    setEnabled(false);
    setMotivo(true);
  };

  /**
   *
   * @param {string} [message]
   * @returns
   */
  const openError = (message) =>
    openNotification({
      title: 'Error',
      content: <div>{message || 'Ha ocurrido un error.'}</div>,
      type: 'error',
      centerButtons: true,
    });

  const sendData = async () => {
    let newStatus = '';

    if (isProcesar) {
      newStatus = ESTADO_DENUNCIA.ENPROCESO;
    } else if (isDesestimar) {
      newStatus = ESTADO_DENUNCIA.DESESTIMADA;
    } else {
      newStatus = ESTADO_DENUNCIA.FINALIZADA;
    }
    const { status, error } = await putStatus({ id: data.id, status: newStatus });
    if (status === 200) {
      if (!isProcesar) {
        const body = { message: texto };
        const { status: statusMessage } = await postMensajesDenuncia({ id: data.id, data: body });
        if (statusMessage === 200) {
          await reloadMensajes();
        }
      }
      openNotification({
        title: 'Estado establecido',
        content: <div>Estado establecido correctamente</div>,
        type: 'success',
        centerButtons: true,
      });

      await reloadComplaint();
      return;
    }
    const { message } = error;
    openError(message || error);
  };

  const onSubmit = () => {
    openNotification({
      title: 'Establecer nuevo estado',
      content: (
        <div>
          Se dispone a establecer un nuevo estado de denuncia. Esta acción es irreversible y no se puede deshacer.
          ¿Estas seguro?
        </div>
      ),
      type: 'confirm',
      okButton: {
        label: 'Establecer',
        mode: 'danger',
        onClick: () => sendData(),
      },
      closeButton: {
        label: 'Cancelar',
      },
    });
  };

  const desasignarDenuncia = async (idAuthority) => {
    const { status, error } = await deleteDesasignarUsuario({ id: data?.id, idAuthority });
    if (status === 200) {
      getAssignedAutoridades();
      openNotification({
        title: 'Denuncia desasignada',
        content: <div>Denuncia desasignada correctamente</div>,
        type: 'success',
        centerButtons: true,
      });
      return;
    }
    const { message } = error;
    openError(message || error);
  };

  const renderContent = (estado) => {
    if (estado === ESTADO_DENUNCIA.ABIERTA) {
      return (
        <>
          <button
            type="button"
            className={!isProcesar ? 'c-sii-gestion-denuncia__button' : 'c-sii-gestion-denuncia__button-selected'}
            onClick={() => onProcesar()}
          >
            <h3>
              <strong>Tramitar</strong>
            </h3>
            <p>Se inicia el proceso de análisis e intercambio de información con el denunciante.</p>
          </button>
          <button
            type="button"
            className={!isDesestimar ? 'c-sii-gestion-denuncia__button' : 'c-sii-gestion-denuncia__button-selected'}
            onClick={() => onDesestimar()}
          >
            <h3>
              <strong>Desestimar</strong>
            </h3>
            <p>Se cierra por falta de pruebas relevantes o considerar que el caso no tiene más recorrido.</p>
          </button>
        </>
      );
    }
    return (
      <>
        <button
          type="button"
          className={!isDesestimar ? 'c-sii-gestion-denuncia__button' : 'c-sii-gestion-denuncia__button-selected'}
          onClick={() => onDesestimar()}
        >
          <h3>
            <strong>Desestimar</strong>
          </h3>
          <p>Se cierra por falta de pruebas relevantes o considerar que el caso no tiene más recorrido.</p>
        </button>
        <button
          type="button"
          className={!isFinalizar ? 'c-sii-gestion-denuncia__button' : 'c-sii-gestion-denuncia__button-selected'}
          onClick={() => onFinalizar()}
        >
          <h3>
            <strong>Finalizar</strong>
          </h3>
          <p>Se finaliza el trámite de la denuncia tras alcanzar una solución al caso.</p>
        </button>
      </>
    );
  };

  useEffect(() => {
    getAssignedAutoridades();
  }, []);

  return (
    <div className="c-sii-gestion-denuncia">
      <h3 className="u-mt-24">
        <strong>Gestión de denuncia</strong>
      </h3>
      {data.status !== ESTADO_DENUNCIA.DESESTIMADA && data.status !== ESTADO_DENUNCIA.FINALIZADA && (
        <div>
          <form autoComplete="off">
            <h3 className="u-mt-16">Seleccione la acción a realizar.</h3>
            <div className="u-mt-16 c-sii-gestion-denuncia__buttons">{renderContent(data.status)}</div>
            {isMotivo && (
              <div className="c-sii-gestion-denuncia__form u-mt-24">
                <AUITextAreaField
                  required
                  onChange={handleChange}
                  label="Indica el motivo"
                  name="descMotivo"
                  controlRef={control}
                  errors={formState.errors}
                />
              </div>
            )}
            <AUIButton
              label="Establecer nuevo estado"
              size="lg"
              disabled={isEnable}
              mode="secondary"
              className="u-mt-24"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            />
          </form>
        </div>
      )}
      <div className="c-sii-gestion-denuncia__autor">
        <div className="c-sii-gestion-denuncia__autor-header u-mt-32">
          <h3>Autoridades asignadas a la denuncia</h3>
          {data.status !== 'DESESTIMADA' && data.status !== 'FINALIZADA' && (
            <AUIButton label="Añadir autoridad" size="lg" mode="secondary" onClick={() => setShow(true)} />
          )}
        </div>
        <AUITable
          columns={getColumns(desasignarDenuncia)}
          data={tableData}
          forcePagination
          pageSize={PAGE_SIZE.LISTADO_GESTION_DENUNCIAS}
          totalData={tableData?.length}
        />
      </div>
      <ModalAsignarDenuncia show={isShow} setShow={() => setShow(false)} reload={getAssignedAutoridades} id={data.id} />
    </div>
  );
};
GestionDenuncia.propTypes = {
  reloadMensajes: func.isRequired,
  reloadComplaint: func.isRequired,
  data: arrayOf(shape({})),
};
GestionDenuncia.defaultProps = {
  data: [],
};

export default GestionDenuncia;
